import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import DualDatePicker from "@/components/DualDatePicker";

function Field({ label, required, children, testId }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="font-heading font-semibold text-slate-900 mb-4 text-lg">{title}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>
      </CardContent>
    </Card>
  );
}

const initialForm = {
  brand: "", product_name: "", customer_name: "", phone_number_1: "",
  customer_address: "", nature_of_complaint: "", nature_of_complaint_is_other: false, warranty_status: "", service_type: "",
  // registered_date_ad is intentionally OMITTED — the server stamps it on create.
  date_of_purchase_ad: "",
  product_model_number: "", other_model_number: "",
  product_category: "", product_size: "", product_specifications: "",
  serial_number_1: "", serial_number_2: "",
  province: "", district_area: "", phone_number_2: "", dealer_name: "",
  // Registered By / Call Source
  registered_by_source: "",
  dealer_contact_person: "", dealer_phone: "",
  // Product physical location
  product_location_type: "",
  product_location_address: "", product_location_area: "",
  product_location_district: "", product_location_province: "",
  product_location_remarks: "",
  warranty_card_url: "", purchase_bill_url: "",
  additional_remarks: "", details_for_technician: "", in_warranty_self_damage: false,
  quantity: 1,
};

export default function ComplaintNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [opts, setOpts] = useState({ brands: [], natures: [], warranty: [], service: [], provinces: [], dealers: [], products: [] });
  const [submitting, setSubmitting] = useState(false);
  const [dup, setDup] = useState(null);
  const [warrantyResult, setWarrantyResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/masters/brands"),
      api.get("/masters/complaint-natures"),
      api.get("/masters/warranty-types"),
      api.get("/masters/service-types"),
      api.get("/masters/provinces"),
      api.get("/masters/dealers"),
      api.get("/products"),
    ]).then(([b, n, w, s, p, d, pr]) => {
      setOpts({
        brands: b.data, natures: n.data, warranty: w.data, service: s.data,
        provinces: p.data, dealers: d.data, products: pr.data,
      });
    });
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Cascading handlers — Brand → Product → Model → auto-fill category/size/specs
  const onBrandChange = (v) => {
    setForm((f) => ({ ...f, brand: v, product_name: "", product_model_number: "", product_category: "", product_size: "", product_specifications: "" }));
  };
  const onProductNameChange = (v) => {
    setForm((f) => ({ ...f, product_name: v, product_model_number: "", product_category: "", product_size: "", product_specifications: "" }));
  };
  const onModelChange = (v) => {
    const match = opts.products.find((p) =>
      p.model === v
      && (!form.brand || p.brand === form.brand)
      && (!form.product_name || p.name === form.product_name)
    );
    setForm((f) => ({
      ...f,
      product_model_number: v,
      product_category: match?.product_category || match?.category || f.product_category,
      product_size: match?.product_size || f.product_size,
      product_specifications: match?.specifications || f.product_specifications,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name?.trim()) { toast.error("Product Name is required"); return; }
    if (!form.product_model_number?.trim()) { toast.error("Product Model Number is required"); return; }
    if (!form.dealer_name?.trim()) { toast.error("Dealer name is required"); return; }
    if (!form.registered_by_source) { toast.error("Registered By / Call Source is required"); return; }
    if (!form.product_location_type) { toast.error("Product Location Type is required"); return; }
    if (form.registered_by_source === "dealer_on_behalf" && !form.dealer_name?.trim()) {
      toast.error("Dealer Name is required when source is 'Dealer on behalf of customer'");
      return;
    }
    if (["dealer_shop", "dealer_warehouse"].includes(form.product_location_type) && !form.dealer_name?.trim()) {
      toast.error("Dealer Name is required when product is at a dealer shop/warehouse");
      return;
    }
    if (!form.warranty_card_url && !form.purchase_bill_url) {
      toast.error("Please upload either the warranty card photo or the purchase bill");
      return;
    }
    if (form.nature_of_complaint_is_other && !form.nature_of_complaint?.trim()) {
      toast.error("Please specify the Nature of Complaint when 'Other' is selected");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints", form);
      // Backend may auto-reopen a closed match and return the EXISTING complaint with _reopened=true.
      if (data._reopened) {
        toast.success(data._message || "Existing complaint reopened", {
          description: `Complaint # ${data.complaint_number}`,
        });
      } else {
        toast.success("Complaint registered", { description: `Complaint # ${data.complaint_number}` });
      }
      navigate(`/complaints/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Server returned a duplicate-OPEN signal (HTTP 409)
      if (err.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_open") {
        setDup({ ...detail });
        setSubmitting(false);
        return;
      }
      toast.error("Could not save", { description: formatApiErrorDetail(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-warranty calc whenever brand/model/category/purchase_date change
  useEffect(() => {
    const ready = form.brand && form.product_model_number && form.date_of_purchase_ad;
    if (!ready) { setWarrantyResult(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/warranty/evaluate", {
          params: {
            brand: form.brand,
            product_category: form.product_category,
            product_name: form.product_name,
            model_number: form.product_model_number,
            date_of_purchase_ad: form.date_of_purchase_ad,
          },
        });
        setWarrantyResult(data);
        // auto-fill the warranty_status if user hasn't picked one
        if (data?.status && (!form.warranty_status || form.warranty_status === "Manual Review")) {
          setForm((f) => ({ ...f, warranty_status: data.status }));
        }
      } catch { /* warranty rules optional */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.brand, form.product_model_number, form.product_category, form.product_name, form.date_of_purchase_ad]);

  const forceCreate = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints?force_create=true", form);
      toast.success("Complaint registered", { description: `Complaint # ${data.complaint_number}` });
      navigate(`/complaints/${data.id}`);
    } catch (err) {
      toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
      setDup(null);
    }
  };

  const reopen = async (cid) => {
    const remarks = prompt("Reopen — describe the new issue:");
    if (!remarks?.trim()) return;
    try {
      await api.post(`/complaints/${cid}/reopen`, { remarks });
      toast.success("Complaint reopened");
      navigate(`/complaints/${cid}`);
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const productNames = Array.from(new Set(
    opts.products.filter((p) => !form.brand || p.brand === form.brand).map((p) => p.name)
  )).sort();
  const productModels = Array.from(new Set(
    opts.products
      .filter((p) => (!form.brand || p.brand === form.brand) && (!form.product_name || p.name === form.product_name))
      .map((p) => p.model).filter(Boolean)
  )).sort();

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="new-complaint-form">
      <Dialog open={!!dup} onOpenChange={(o) => !o && setDup(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="duplicate-dialog-title">
              {dup?.code === "duplicate_open" ? "Open complaint already exists" : "Previous complaint found"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{dup?.message}</p>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="duplicate-list">
            {(dup?.matches || []).map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-md p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold">{m.complaint_number}</span>
                  <span className="text-xs text-slate-500">{m.status} · {m.registered_date_ad}</span>
                </div>
                <div className="text-xs text-slate-700 mt-0.5">{m.brand} · {m.product_name} · {m.product_model_number}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" type="button"
                          onClick={() => { setDup(null); navigate(`/complaints/${m.id}`); }}
                          data-testid={`open-existing-${m.complaint_number}`}>Open complaint</Button>
                  {dup?.code === "duplicate_closed" && (
                    <Button size="sm" type="button" className="bg-slate-900 text-white"
                            onClick={() => reopen(m.id)}
                            data-testid={`reopen-${m.complaint_number}`}>Reopen this complaint</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setDup(null)}>Cancel</Button>
            <Button type="button" onClick={forceCreate} className="bg-rose-600 text-white"
                    data-testid="force-create-btn" disabled={submitting}>
              Create new anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">New Complaint</h1>
          <p className="text-sm text-slate-500 mt-1">Register a new service complaint. Required fields marked with *.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/complaints")} data-testid="cancel-btn">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-complaint-btn">
            {submitting ? "Saving..." : "Register Complaint"}
          </Button>
        </div>
      </div>

      {warrantyResult && (
        <div className={`rounded-md border px-4 py-2 text-sm flex items-start gap-3 ${warrantyResult.status === "In Warranty" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : warrantyResult.status === "Out of Warranty" ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}
             data-testid="warranty-banner">
          <div className="font-semibold whitespace-nowrap">Warranty: {warrantyResult.status}</div>
          <div className="text-xs flex-1">{warrantyResult.reason}{warrantyResult.days_remaining != null && <span> · {warrantyResult.days_remaining} days remaining</span>}</div>
        </div>
      )}

      <Section title="Brand & Product">
        <Field label="Brand" required>
          <Select value={form.brand} onValueChange={onBrandChange}>
            <SelectTrigger data-testid="brand-select"><SelectValue placeholder="Select brand" /></SelectTrigger>
            <SelectContent>{opts.brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Product Name" required>
          <Select value={form.product_name} onValueChange={onProductNameChange} disabled={!form.brand}>
            <SelectTrigger data-testid="product-select"><SelectValue placeholder={form.brand ? "Select product" : "Pick brand first"} /></SelectTrigger>
            <SelectContent>
              {productNames.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No products for this brand</div>}
              {productNames.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Product Model Number" required>
          <Select value={form.product_model_number} onValueChange={onModelChange} disabled={!form.product_name}>
            <SelectTrigger data-testid="model-select"><SelectValue placeholder={form.product_name ? "Select model" : "Pick product first"} /></SelectTrigger>
            <SelectContent className="max-h-72">
              {productModels.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No models in master — enter via Settings → Products</div>}
              {productModels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Product Category"><Input value={form.product_category} onChange={(e) => update("product_category", e.target.value)} placeholder="Auto-filled from model" data-testid="product-category-input" /></Field>
        <Field label="Product Size"><Input value={form.product_size} onChange={(e) => update("product_size", e.target.value)} placeholder='e.g. 1.5 Ton, 55 L, 43"' data-testid="product-size-input" /></Field>
        <Field label="Specifications"><Input value={form.product_specifications} onChange={(e) => update("product_specifications", e.target.value)} placeholder="Auto-filled from model" /></Field>
        <Field label="Other Model Number"><Input value={form.other_model_number} onChange={(e) => update("other_model_number", e.target.value)} /></Field>
        <Field label="Serial Number 1"><Input value={form.serial_number_1} onChange={(e) => update("serial_number_1", e.target.value)} /></Field>
        <Field label="Serial Number 2"><Input value={form.serial_number_2} onChange={(e) => update("serial_number_2", e.target.value)} /></Field>
        <Field label="Quantity"><Input type="number" min={1} value={form.quantity} onChange={(e) => update("quantity", parseInt(e.target.value)||1)} /></Field>
        <Field label="Date of Purchase">
          <DualDatePicker value={form.date_of_purchase_ad} onChange={(v) => update("date_of_purchase_ad", v)} testId="purchase-date" />
        </Field>
        <Field label="Registration Date">
          <div className="h-9 flex items-center text-xs text-slate-500 italic" data-testid="registered-date-readonly">
            Auto-stamped by server when you click Register
          </div>
        </Field>
      </Section>

      <Section title="Customer Details">
        <Field label="Customer Name" required><Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="customer-name-input" /></Field>
        <Field label="Phone Number 1" required><Input value={form.phone_number_1} onChange={(e) => update("phone_number_1", e.target.value)} data-testid="phone-1-input" /></Field>
        <Field label="Phone Number 2"><Input value={form.phone_number_2} onChange={(e) => update("phone_number_2", e.target.value)} /></Field>
        <Field label="Province">
          <Select value={form.province} onValueChange={(v) => update("province", v)}>
            <SelectTrigger><SelectValue placeholder="Select province" /></SelectTrigger>
            <SelectContent>{opts.provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="District / Area"><Input value={form.district_area} onChange={(e) => update("district_area", e.target.value)} /></Field>
        <Field label="Customer Address" required>
          <Textarea value={form.customer_address} onChange={(e) => update("customer_address", e.target.value)} data-testid="address-input" />
        </Field>
        <Field label="Dealer Name" required>
          <Select value={form.dealer_name} onValueChange={(v) => {
            update("dealer_name", v);
            // If a dealer with full address is chosen and product location is at
            // the dealer shop/warehouse, auto-fill the address-related fields
            // (only when they are currently empty — never overwrite user edits).
            const dealer = opts.dealers.find((d) => d.name === v);
            if (dealer && ["dealer_shop", "dealer_warehouse"].includes(form.product_location_type)) {
              setForm((f) => ({
                ...f,
                dealer_name: v,
                product_location_address: f.product_location_address || dealer.address || "",
                product_location_area:    f.product_location_area    || dealer.area    || "",
                product_location_district:f.product_location_district|| dealer.district|| "",
                product_location_province:f.product_location_province|| dealer.province|| "",
                dealer_contact_person:    f.dealer_contact_person    || dealer.contact_person || "",
                dealer_phone:             f.dealer_phone             || dealer.phone || "",
              }));
            }
          }}>
            <SelectTrigger data-testid="dealer-select"><SelectValue placeholder="Select dealer" /></SelectTrigger>
            <SelectContent>{opts.dealers.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </Section>

      <Section title="Registration Source & Product Location">
        <Field label="Registered By / Call Source" required>
          <Select value={form.registered_by_source} onValueChange={(v) => update("registered_by_source", v)}>
            <SelectTrigger data-testid="registered-by-source-select"><SelectValue placeholder="Who registered this complaint?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer_direct">Customer directly</SelectItem>
              <SelectItem value="dealer_on_behalf">Dealer on behalf of customer</SelectItem>
              <SelectItem value="company_internal">Company / internal staff</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {form.registered_by_source === "dealer_on_behalf" && (
          <>
            <Field label="Dealer Contact Person">
              <Input value={form.dealer_contact_person} onChange={(e) => update("dealer_contact_person", e.target.value)} data-testid="dealer-contact-person-input" placeholder="e.g. Mr. Ram" />
            </Field>
            <Field label="Dealer Phone">
              <Input value={form.dealer_phone} onChange={(e) => update("dealer_phone", e.target.value)} data-testid="dealer-phone-input" />
            </Field>
          </>
        )}

        <Field label="Product Location Type" required>
          <Select value={form.product_location_type} onValueChange={(v) => {
            // If user switches TO dealer shop/warehouse and a dealer is already
            // selected, copy its address into the empty product-location fields.
            const dealer = opts.dealers.find((d) => d.name === form.dealer_name);
            if (dealer && ["dealer_shop", "dealer_warehouse"].includes(v)) {
              setForm((f) => ({
                ...f,
                product_location_type: v,
                product_location_address: f.product_location_address || dealer.address || "",
                product_location_area:    f.product_location_area    || dealer.area    || "",
                product_location_district:f.product_location_district|| dealer.district|| "",
                product_location_province:f.product_location_province|| dealer.province|| "",
              }));
            } else {
              update("product_location_type", v);
            }
          }}>
            <SelectTrigger data-testid="product-location-type-select"><SelectValue placeholder="Where is the product?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer_place">Customer place</SelectItem>
              <SelectItem value="dealer_shop">Dealer shop</SelectItem>
              <SelectItem value="dealer_warehouse">Dealer warehouse</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Product Location Address">
          <Textarea
            value={form.product_location_address}
            onChange={(e) => update("product_location_address", e.target.value)}
            data-testid="product-location-address-input"
            placeholder={form.product_location_type === "customer_place" ? "Defaults to customer address if left empty" : "Address at dealer / other location"}
            rows={2}
          />
        </Field>
        <Field label="Area / Locality">
          <Input value={form.product_location_area} onChange={(e) => update("product_location_area", e.target.value)} data-testid="product-location-area-input" />
        </Field>
        <Field label="District">
          <Input value={form.product_location_district} onChange={(e) => update("product_location_district", e.target.value)} data-testid="product-location-district-input" />
        </Field>
        <Field label="Province">
          <Input value={form.product_location_province} onChange={(e) => update("product_location_province", e.target.value)} data-testid="product-location-province-input" />
        </Field>
        <Field label="Location Remarks">
          <Input value={form.product_location_remarks} onChange={(e) => update("product_location_remarks", e.target.value)} data-testid="product-location-remarks-input" placeholder="Landmark / contact at site" />
        </Field>
      </Section>

      <Section title="Proof of Purchase">
        <div className="md:col-span-3 -mb-2 text-xs text-slate-600">
          At least one of <span className="font-semibold">Warranty Card</span> or <span className="font-semibold">Purchase Bill</span> is required. Images (PNG/JPG) or PDF, up to 4 MB each.
        </div>
        <Field label="Warranty Card Photo" testId="warranty-card-field">
          <FileUploadField value={form.warranty_card_url} onChange={(v) => update("warranty_card_url", v)}
                           testId="warranty-card-input" />
        </Field>
        <Field label="Purchase Bill (Photo or PDF)" testId="purchase-bill-field">
          <FileUploadField value={form.purchase_bill_url} onChange={(v) => update("purchase_bill_url", v)}
                           testId="purchase-bill-input" accept="image/*,application/pdf" />
        </Field>
      </Section>

      <Section title="Service Information">
        <Field label="Nature of Complaint" required>
          <Select
            value={form.nature_of_complaint === "__OTHER__" || (form.nature_of_complaint && !opts.natures.some((n) => n.name === form.nature_of_complaint) && form.nature_of_complaint_is_other) ? "__OTHER__" : form.nature_of_complaint}
            onValueChange={(v) => {
              if (v === "__OTHER__") {
                update("nature_of_complaint_is_other", true);
                update("nature_of_complaint", "");
              } else {
                update("nature_of_complaint_is_other", false);
                update("nature_of_complaint", v);
              }
            }}>
            <SelectTrigger data-testid="nature-select"><SelectValue placeholder="Select nature" /></SelectTrigger>
            <SelectContent>
              {opts.natures.map((n) => <SelectItem key={n.id} value={n.name}>{n.name}</SelectItem>)}
              <SelectItem key="__OTHER__" value="__OTHER__">Other (specify…)</SelectItem>
            </SelectContent>
          </Select>
          {form.nature_of_complaint_is_other && (
            <Input
              className="mt-2"
              value={form.nature_of_complaint}
              onChange={(e) => update("nature_of_complaint", e.target.value)}
              placeholder="Specify Nature of Complaint (required)"
              data-testid="nature-other-input"
            />
          )}
        </Field>
        <Field label="Warranty Status" required>
          <Select value={form.warranty_status} onValueChange={(v) => update("warranty_status", v)}>
            <SelectTrigger data-testid="warranty-select"><SelectValue placeholder="Select warranty" /></SelectTrigger>
            <SelectContent>{opts.warranty.map((w) => <SelectItem key={w.id} value={w.name}>{w.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Service Type" required>
          <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
            <SelectTrigger data-testid="service-type-select"><SelectValue placeholder="Select service type" /></SelectTrigger>
            <SelectContent>{opts.service.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="In-Warranty Self-Damage">
          <div className="flex items-center gap-2 h-9">
            <Checkbox checked={form.in_warranty_self_damage} onCheckedChange={(v) => update("in_warranty_self_damage", !!v)} data-testid="self-damage-checkbox" />
            <span className="text-sm text-slate-700">Mark as self-damage</span>
          </div>
        </Field>
        <Field label="Additional Remarks">
          <Textarea value={form.additional_remarks} onChange={(e) => update("additional_remarks", e.target.value)} rows={2} />
        </Field>
        <Field label="Details for Technician">
          <Textarea value={form.details_for_technician} onChange={(e) => update("details_for_technician", e.target.value)} rows={2} />
        </Field>
      </Section>
    </form>
  );
}


function FileUploadField({ value, onChange, testId, accept = "image/*,application/pdf" }) {
  const [busy, setBusy] = React.useState(false);
  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Max 4 MB"); return; }
    const reader = new FileReader();
    setBusy(true);
    reader.onload = () => { onChange(reader.result); setBusy(false); };
    reader.onerror = () => { toast.error("Could not read file"); setBusy(false); };
    reader.readAsDataURL(file);
  };
  const isImage = value && value.startsWith("data:image/");
  const isPdf = value && value.startsWith("data:application/pdf");
  return (
    <div className="space-y-2">
      <input type="file" accept={accept} onChange={onPick} className="block w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-200 file:text-slate-800"
             data-testid={testId} />
      {busy && <div className="text-xs text-slate-500">Reading file…</div>}
      {isImage && (
        <div className="flex items-start gap-2">
          <img src={value} alt="preview" className="h-16 w-16 object-cover rounded border" />
          <button type="button" onClick={() => onChange("")}
                  className="text-xs text-rose-600 hover:underline" data-testid={`${testId}-clear`}>Remove</button>
        </div>
      )}
      {isPdf && (
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 bg-slate-100 rounded text-slate-700">PDF attached</span>
          <button type="button" onClick={() => onChange("")}
                  className="text-rose-600 hover:underline" data-testid={`${testId}-clear`}>Remove</button>
        </div>
      )}
    </div>
  );
}
